import classNames from 'classnames';
import { ButtonMenu } from 'components/actions/ButtonMenu';
import { ContainerListItem } from 'components/actions/ListItem/ListItem.view';
import { Badge } from 'components/containers/Badge';
import { Icon } from 'components/images/Icon/Icon';
import { SocialProfile } from 'components/socialProfiles/SocialProfile';
import { BadgeColorsEnum, BadgeSizesEnum } from 'constants/badge';
import { IconsEnum } from 'constants/icons';
import { SquareButtonIconsEnum } from 'constants/square-button-icons';
import { AutoImportPostTiming, AutoImportStatus } from 'models/graphql';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';
import React from 'react';
import { AutoImportQueryResult } from '../AutoImporters.view';
import classes from './ImporterCard.module.scss';
import { usePauseSettings } from 'hooks/usePauseSettings';
import { Debugger } from '../../../../components/containers/Debugger';

export type ImporterCardViewProps = {
  autoImporter: AutoImportQueryResult;
  openModalDelete: VoidFunction;
  openModalPause: VoidFunction;
  openModalEdit: VoidFunction;
};

const importTypeMapping = {
  [AutoImportPostTiming.DRAFT]: 'Draft',
  [AutoImportPostTiming.QUEUE_BOTTOM]: 'Bottom of Queue',
  [AutoImportPostTiming.QUEUE_TOP]: 'Top of Queue',
  [AutoImportPostTiming.NOW]: 'Now'
};

export const ImporterCardView = ({
  autoImporter,
  openModalDelete,
  openModalPause,
  openModalEdit
}: ImporterCardViewProps) => {
  const { name, url, status, category, postTiming, recycle, socialProfiles } = autoImporter;
  const { autoImportersPaused } = usePauseSettings();
  const ButtonMenuListItems: ContainerListItem[] = [
    {
      action: () => openModalEdit(),
      label: 'Edit'
    },
    ...(autoImportersPaused
      ? []
      : [
          {
            action: () => openModalPause(),
            label: status === AutoImportStatus.ACTIVE ? 'Pause' : 'Un-pause'
          }
        ]),
    {
      action: () => openModalDelete(),
      label: 'Delete'
    }
  ];

  return (
    <div className={classes.ImporterCard}>
      <Debugger tooltipContent={'Auto Importer ID: ' + autoImporter.id} modalContent={autoImporter} />
      <div className={classes.HeaderContent}>
        <div className={classNames('grid-x align-left', classes.HeaderGrid)}>
          <div className={'cell auto'}>
            <h4 className={classes.ImporterName}>{name}</h4>
          </div>
          {!autoImportersPaused && (
            <div className={'cell shrink'}>
              <div className={classes.BadgeWrapper}>
                <Badge
                  color={status === 'ACTIVE' ? BadgeColorsEnum.GREEN15 : BadgeColorsEnum.GREY20}
                  size={BadgeSizesEnum.LARGE}
                  text={status}
                />
              </div>
            </div>
          )}
        </div>
        <div className={classes.ImporterLink}>
          <a href={url} target={'_blank'} rel={'noreferrer'}>
            {url}
          </a>
        </div>
        <div className={classes.ButtonMenuContainer}>
          <ButtonMenu
            isSquareButton={true}
            buttonType={'transparent'}
            listItems={ButtonMenuListItems}
            side={'right'}
            icon={SquareButtonIconsEnum.dotsShifted}
          />
        </div>
      </div>

      <div className={classes.SocialProfiles}>
        {socialProfiles &&
          socialProfiles.map(profile => (
            <SocialProfile
              key={profile.id}
              size={'small'}
              socialProfile={{
                id: profile.id,
                provider: profilesHelpers.mapOAuthFromTypename(profile.__typename),
                network: profilesHelpers.mapNetworkFromTypename(profile.__typename),
                genericProfile: profile.__typename === 'GenericSocialProfile' ? profile.genericProfile : undefined,
                name: profile.displayName
              }}
            />
          ))}
      </div>
      <div className={classes.CardFooter}>
        <div className={classNames('grid-x', classes.CategoryContainer)}>
          <div className={'cell auto'}>
            <div className={classes.Category}>
              <div className={classNames(classes.CategoryColor, classes[`CategoryColor${category.color}`])}></div>
              <div className={classNames('one-liner', classes.CategoryName)}>{category.name}</div>
            </div>
          </div>
          <div className={'cell shrink'}>
            <div className={classes.ImportType}>{importTypeMapping[postTiming]}</div>
          </div>
          <div className={'cell shrink'}>
            {recycle && (
              <div className={classes.Recycle}>
                <Icon name={IconsEnum.cycles} size={'medium'}></Icon> <span>Recycle</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
